import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RefreshCw, MessageSquare, Clock, Type, Mail, Plus, Trash2, Edit, X, Server, Check } from 'lucide-react';
import apiService from '../../services/api';

const PatientSettings = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('general');
  const p = 'patient.adminSettings';

  const sections = [
    { id: 'general', label: t(`${p}.generalTab`), icon: MessageSquare },
    { id: 'topics', label: t(`${p}.topicsTab`), icon: Type },
    { id: 'smtp', label: t(`${p}.smtpTab`), icon: Server },
    { id: 'emails', label: t(`${p}.emailsTab`), icon: Mail },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              style={{
                padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: activeSection === s.id ? '600' : '400',
                background: activeSection === s.id ? '#fef3c7' : '#f3f4f6',
                color: activeSection === s.id ? '#d97706' : '#6b7280',
                border: activeSection === s.id ? '1px solid #fcd34d' : '1px solid transparent',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}>
              <Icon size={14} /> {s.label}
            </button>
          );
        })}
      </div>

      {activeSection === 'general' && <GeneralSection />}
      {activeSection === 'topics' && <TopicTypesSection />}
      {activeSection === 'smtp' && <SmtpSection />}
      {activeSection === 'emails' && <EmailsSection />}
    </div>
  );
};

// =====================================================
// GENERAL SETTINGS
// =====================================================
const GeneralSection = () => {
  const { t } = useTranslation();
  const p = 'patient.adminSettings';
  const [settings, setSettings] = useState({ patientWelcomeMessage: '', patientOfflineMessage: '', patientWidgetTitle: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.request('/admin/settings/app').then(data => {
      const m = {}; data.forEach(s => { m[s.key] = s.value; });
      setSettings({
        patientWelcomeMessage: m.patientWelcomeMessage || '',
        patientOfflineMessage: m.patientOfflineMessage || '',
        patientWidgetTitle: m.patientWidgetTitle || '',
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    await apiService.request('/admin/settings/app', { method: 'PUT', body: JSON.stringify({ settings: [
      { key: 'patientWelcomeMessage', value: settings.patientWelcomeMessage },
      { key: 'patientOfflineMessage', value: settings.patientOfflineMessage },
      { key: 'patientWidgetTitle', value: settings.patientWidgetTitle },
    ]})});
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Card title={t(`${p}.widgetTitle`)} icon={Type}>
        <input type="text" value={settings.patientWidgetTitle}
          onChange={e => setSettings({...settings, patientWidgetTitle: e.target.value})}
          style={inputStyle} />
      </Card>
      <Card title={t(`${p}.welcomeMessage`)} icon={MessageSquare} desc={t(`${p}.welcomeMessageDesc`)}>
        <textarea value={settings.patientWelcomeMessage} rows={3}
          onChange={e => setSettings({...settings, patientWelcomeMessage: e.target.value})}
          style={{...inputStyle, resize: 'vertical'}} />
      </Card>
      <Card title={t(`${p}.offlineMessage`)} icon={Clock} desc={t(`${p}.offlineMessageDesc`)}>
        <textarea value={settings.patientOfflineMessage} rows={3}
          onChange={e => setSettings({...settings, patientOfflineMessage: e.target.value})}
          style={{...inputStyle, resize: 'vertical'}} />
      </Card>
      <SaveButton saving={saving} saved={saved} onClick={save} />
    </div>
  );
};

// =====================================================
// TOPIC TYPES
// =====================================================
const TopicTypesSection = () => {
  const { t } = useTranslation();
  const p = 'patient.adminSettings';
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', labelTr: '', labelEn: '', sortOrder: 0, isActive: true });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    apiService.request('/patient/admin/topic-types').then(setTopics).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    if (editing) {
      await apiService.request(`/patient/admin/topic-types/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) });
    } else {
      await apiService.request('/patient/admin/topic-types', { method: 'POST', body: JSON.stringify(form) });
    }
    setSaving(false); setEditing(null);
    setForm({ code: '', labelTr: '', labelEn: '', sortOrder: topics.length, isActive: true });
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t(`${p}.confirmDeleteTopic`))) return;
    await apiService.request(`/patient/admin/topic-types/${id}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Card title={t(`${p}.topicTypes`)} icon={Type} desc={t(`${p}.topicTypesDesc`)}>
        <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
          <input placeholder={t(`${p}.code`)} value={form.code} required
            onChange={e => setForm({...form, code: e.target.value})} style={inputStyle} />
          <input placeholder={t(`${p}.sortOrder`)} type="number" value={form.sortOrder}
            onChange={e => setForm({...form, sortOrder: parseInt(e.target.value)||0})} style={inputStyle} />
          <input placeholder={t(`${p}.labelTr`)} value={form.labelTr} required
            onChange={e => setForm({...form, labelTr: e.target.value})} style={inputStyle} />
          <input placeholder={t(`${p}.labelEn`)} value={form.labelEn} required
            onChange={e => setForm({...form, labelEn: e.target.value})} style={inputStyle} />
          <div style={{ gridColumn: '1/-1', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive: e.target.checked})} /> {t(`${p}.active`)}
            </label>
            <div style={{ flex: 1 }} />
            {editing && <button type="button" onClick={() => { setEditing(null); setForm({ code:'',labelTr:'',labelEn:'',sortOrder:topics.length,isActive:true }); }}
              style={{ padding: '8px 14px', background: '#f3f4f6', borderRadius: '6px', fontSize: '13px' }}>{t(`${p}.cancel`)}</button>}
            <button type="submit" disabled={saving}
              style={{ padding: '8px 14px', background: '#f59e0b', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '500' }}>
              {editing ? t(`${p}.update`) : t(`${p}.add`)}
            </button>
          </div>
        </form>

        {topics.map(tp => (
          <div key={tp.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#f9fafb',
            borderRadius: '8px', marginBottom: '6px', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: tp.isActive ? '#22c55e' : '#d1d5db' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>{tp.labelTr} / {tp.labelEn}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{t(`${p}.code`)}: {tp.code} | {t(`${p}.sortOrder`)}: {tp.sortOrder}</div>
            </div>
            <button onClick={() => { setEditing(tp); setForm(tp); }}
              style={{ padding: '6px', background: '#f3f4f6', borderRadius: '6px' }}><Edit size={14} /></button>
            <button onClick={() => del(tp.id)}
              style={{ padding: '6px', background: '#fef2f2', borderRadius: '6px' }}><Trash2 size={14} color="#dc2626" /></button>
          </div>
        ))}
      </Card>
    </div>
  );
};

// =====================================================
// SMTP SETTINGS
// =====================================================
const SmtpSection = () => {
  const { t } = useTranslation();
  const p = 'patient.adminSettings';
  const [smtp, setSmtp] = useState({ smtpServer:'', smtpPort:'587', smtpUser:'', smtpPassword:'', smtpFromEmail:'', smtpFromName:'ChatBot', smtpUseSsl:'true' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.request('/admin/settings/app').then(data => {
      const m = {}; data.forEach(s => { m[s.key] = s.value; });
      setSmtp({
        smtpServer: m.smtpServer || '', smtpPort: m.smtpPort || '587',
        smtpUser: m.smtpUser || '', smtpPassword: m.smtpPassword || '',
        smtpFromEmail: m.smtpFromEmail || '', smtpFromName: m.smtpFromName || 'ChatBot',
        smtpUseSsl: m.smtpUseSsl || 'true'
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    const settings = Object.entries(smtp).map(([key, value]) => ({ key, value }));
    await apiService.request('/admin/settings/app', { method: 'PUT', body: JSON.stringify({ settings }) });
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Card title={t(`${p}.smtpTab`)} icon={Server} desc={t(`${p}.smtpDesc`)}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpServer`)}</label>
            <input value={smtp.smtpServer} placeholder="smtp.gmail.com"
              onChange={e => setSmtp({...smtp, smtpServer: e.target.value})} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpPort`)}</label>
            <input value={smtp.smtpPort} placeholder="587"
              onChange={e => setSmtp({...smtp, smtpPort: e.target.value})} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpUser`)}</label>
            <input value={smtp.smtpUser} placeholder="user@gmail.com"
              onChange={e => setSmtp({...smtp, smtpUser: e.target.value})} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpPassword`)}</label>
            <input type="password" value={smtp.smtpPassword} placeholder="****"
              onChange={e => setSmtp({...smtp, smtpPassword: e.target.value})} style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpFromEmail`)}</label>
            <input value={smtp.smtpFromEmail} placeholder="noreply@klinik.com"
              onChange={e => setSmtp({...smtp, smtpFromEmail: e.target.value})} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>{t(`${p}.smtpFromName`)}</label>
            <input value={smtp.smtpFromName} placeholder="ChatBot"
              onChange={e => setSmtp({...smtp, smtpFromName: e.target.value})} style={inputStyle} />
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginBottom: '12px' }}>
          <input type="checkbox" checked={smtp.smtpUseSsl === 'true'}
            onChange={e => setSmtp({...smtp, smtpUseSsl: e.target.checked ? 'true' : 'false'})} />
          {t(`${p}.smtpUseSsl`)}
        </label>
      </Card>
      <SaveButton saving={saving} saved={saved} onClick={save} />
    </div>
  );
};

// =====================================================
// NOTIFICATION EMAILS
// =====================================================
const EmailsSection = () => {
  const { t } = useTranslation();
  const p = 'patient.adminSettings';
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const load = () => {
    setLoading(true);
    apiService.request('/patient/admin/notification-emails').then(setEmails).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newEmail.trim()) return;
    await apiService.request('/patient/admin/notification-emails', {
      method: 'POST', body: JSON.stringify({ email: newEmail, label: newLabel, isActive: true })
    });
    setNewEmail(''); setNewLabel(''); load();
  };

  const toggle = async (em) => {
    await apiService.request(`/patient/admin/notification-emails/${em.id}`, {
      method: 'PUT', body: JSON.stringify({ ...em, isActive: !em.isActive })
    });
    load();
  };

  const del = async (id) => {
    if (!window.confirm(t(`${p}.confirmDeleteEmail`))) return;
    await apiService.request(`/patient/admin/notification-emails/${id}`, { method: 'DELETE' });
    load();
  };

  if (loading) return <Loader />;

  return (
    <div>
      <Card title={t(`${p}.notifEmails`)} icon={Mail} desc={t(`${p}.notifEmailsDesc`)}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <input value={newEmail} placeholder={t(`${p}.emailPlaceholder`)} type="email"
            onChange={e => setNewEmail(e.target.value)}
            style={{ ...inputStyle, flex: 2 }} />
          <input value={newLabel} placeholder={t(`${p}.labelPlaceholder`)}
            onChange={e => setNewLabel(e.target.value)}
            style={{ ...inputStyle, flex: 1 }} />
          <button onClick={add} disabled={!newEmail.trim()}
            style={{ padding: '10px 14px', background: newEmail.trim() ? '#f59e0b' : '#e5e7eb', color: 'white',
              borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '500' }}>
            <Plus size={14} /> {t(`${p}.add`)}
          </button>
        </div>

        {emails.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>
            {t(`${p}.noEmails`)}
          </div>
        ) : emails.map(em => (
          <div key={em.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', background: '#f9fafb',
            borderRadius: '8px', marginBottom: '6px', gap: '10px' }}>
            <button onClick={() => toggle(em)} title={em.isActive ? 'Disable' : 'Enable'}
              style={{ width: '24px', height: '24px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: em.isActive ? '#dcfce7' : '#f3f4f6', border: 'none', cursor: 'pointer' }}>
              {em.isActive && <Check size={14} color="#22c55e" />}
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: em.isActive ? '#1f2937' : '#9ca3af' }}>{em.email}</div>
              {em.label && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{em.label}</div>}
            </div>
            <button onClick={() => del(em.id)}
              style={{ padding: '6px', background: '#fef2f2', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>
              <Trash2 size={14} color="#dc2626" />
            </button>
          </div>
        ))}
      </Card>
    </div>
  );
};

// =====================================================
// SHARED COMPONENTS
// =====================================================
const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb',
  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none'
};

const labelStyle = { display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#6b7280' };

const Card = ({ title, icon: Icon, desc, children }) => (
  <div style={{ padding: '20px', background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: desc ? '4px' : '14px' }}>
      <Icon size={18} color="#f59e0b" />
      <h4 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>{title}</h4>
    </div>
    {desc && <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '14px', marginTop: 0 }}>{desc}</p>}
    {children}
  </div>
);

const Loader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
    <RefreshCw size={24} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />
  </div>
);

const SaveButton = ({ saving, saved, onClick }) => {
  const { t } = useTranslation();
  const p = 'patient.adminSettings';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <button onClick={onClick} disabled={saving}
        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px',
          background: saving ? '#9ca3af' : '#f59e0b', color: 'white', borderRadius: '8px', fontSize: '14px', fontWeight: '500' }}>
        <Save size={16} /> {saving ? t(`${p}.saving`) : t(`${p}.save`)}
      </button>
      {saved && <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '500' }}>{t(`${p}.saved`)}</span>}
    </div>
  );
};

export default PatientSettings;
